/**
 * SalesOrdersPage — Pedidos de Venta
 * Lista, crea, edita, aprueba, factura y anula pedidos de venta.
 */
import { ordenControlado } from '@/components/ds/ordenControlado'
import { sortStateToParam, type SortState } from '@/components/SortableHeader'
import { formatMoneyForTenant } from '@/lib/locale'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search, Plus, ClipboardList, MoreVertical, Eye, Pencil, Check, FileText, X, Copy, Download,
} from 'lucide-react'
import {
  useSalesOrders, useApproveSalesOrder, useInvoiceSalesOrder, useCancelSalesOrder, useCloneSalesOrder,
  openSalesOrderPdf,
  type SalesOrderListItem, type SalesOrderStatus,
} from '../hooks/useSales'
import { usePermission } from '@/hooks/usePermission'
import { KebabMenu } from '@/components/KebabMenu'
import { DataTable, type DataTableColumn } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { PageHeader } from '@/components/HelpPanel'
import { ToastContainer, useToast } from '@/components/Toast'
import { extractErrorMessage } from '@/lib/errorUtils'
import { useAuthStore } from '@/store/authStore'
import { HELP } from '@/data/helpContent'
import { SalesOrderDetailModal } from './SalesOrderDetailModal'
import { AnularPedidoDialog } from './AnularPedidoDialog'
import { Pagination } from '@/components/ds/Pagination'
import { useTenantLocale } from '@/lib/locale'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { cls: string; dotCls: string; label: string }> = {
  draft:     { cls: 'bg-surface-muted text-content-muted',    dotCls: 'bg-content-subtle',    label: 'Borrador' },
  approved:  { cls: 'bg-primary-50 text-primary-hover',       dotCls: 'bg-primary',     label: 'Aprobado' },
  invoiced:  { cls: 'bg-success-surface text-success-content', dotCls: 'bg-success-solid',  label: 'Facturado' },
  cancelled: { cls: 'bg-danger-surface text-danger-content',         dotCls: 'bg-danger-solid',      label: 'Anulado' },
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotCls}`} />
      {cfg.label}
    </span>
  )
}

const fmtMoney = (value: string | number | undefined) => {
  if (value === undefined || value === null) return '—'
  return formatMoneyForTenant(typeof value === 'string' ? parseFloat(value) : value)
}

const STATUS_FILTERS = [
  { value: '',          label: 'Todos' },
  { value: 'draft',     label: 'Borrador' },
  { value: 'approved',  label: 'Aprobado' },
  { value: 'invoiced',  label: 'Facturado' },
  { value: 'cancelled', label: 'Anulado' },
]

// ─── OrderRow ─────────────────────────────────────────────────────────────────

/**
 * Las columnas del listado de pedidos.
 *
 * 🔴 Esto era `OrderRow`, un componente que emitía su propio `<tr>` — el bloqueo
 * «componente-fila» del inventario. Era render puro, así que se desarma en
 * columnas sin perder nada.
 */
function columnasDePedido({
  isSellerOnly, currentUserId, onView, onEdit, onApprove, onInvoice, onClone, onCancel,
}: {
  isSellerOnly: boolean
  currentUserId?: string
  onView: (o: SalesOrderListItem) => void
  onEdit: (o: SalesOrderListItem) => void
  onApprove: (o: SalesOrderListItem) => void
  onInvoice: (o: SalesOrderListItem) => void
  onClone: (o: SalesOrderListItem) => void
  onCancel: (o: SalesOrderListItem) => void
}): DataTableColumn<SalesOrderListItem>[] {
  return [
    { key: 'order_number', header: 'N° Pedido', sortable: true,
      render: o => (
        <p className="text-sm font-mono font-semibold text-content">#{o.order_number}</p>
      ) },
    { key: 'customer_name', header: 'Cliente', sortable: true,
      render: o => <p className="text-sm font-medium text-content">{o.customer_name}</p> },
    { key: 'seller_name', header: 'Vendedor', sortable: true,
      render: o => <p className="text-sm text-content-muted">{o.seller_name || '—'}</p> },
    { key: 'target_document_type', header: 'Tipo Doc', sortable: true,
      render: o => (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold font-mono bg-surface-muted text-content-muted">
          {o.target_document_type || '—'}
        </span>
      ) },
    { key: 'total', header: 'Total', numeric: true, sortable: true,
      sortValue: o => Number(o.total),
      render: o => <p className="text-sm font-semibold text-content">{fmtMoney(o.total)}</p> },
    { key: 'commission_total', header: 'Comisión', numeric: true, sortable: true,
      sortValue: o => Number(o.commission_total),
      render: o => <p className="text-sm text-content-muted">{fmtMoney(o.commission_total)}</p> },
    { key: 'order_date', header: 'Fecha', sortable: true,
      render: o => <p className="text-sm text-content-muted">{o.order_date || '—'}</p> },
    { key: 'status', header: 'Estado', sortable: true,
      render: o => <StatusBadge status={o.status} /> },
    { key: 'acciones', header: '', width: 56,
      render: o => {
        const esPedidoPropio = !!currentUserId && o.seller === currentUserId
        return (
          <div className="text-right" onClick={e => e.stopPropagation()}>
            <KebabMenu items={[
              { label: 'Ver detalle', icon: <Eye size={14} />, onClick: () => onView(o) },
              { label: 'Cotización (PDF)', icon: <Download size={14} />, onClick: () => { void openSalesOrderPdf(o.id) } },
              { label: 'Editar', icon: <Pencil size={14} />, onClick: () => onEdit(o), hidden: o.status !== 'draft' },
              {
                // El backend rechaza aprobar tu propio pedido (segregación de
                // funciones, `ApproveSalesOrderView`). Tener el permiso no alcanza,
                // así que ocultarla evita ofrecer una acción que siempre rebota.
                label: 'Aprobar',
                icon: <Check size={14} />,
                onClick: () => onApprove(o),
                hidden: o.status !== 'draft' || isSellerOnly || esPedidoPropio,
                className: 'text-primary hover:bg-primary-50',
              },
              { label: 'Facturar', icon: <FileText size={14} />, onClick: () => onInvoice(o), hidden: o.status !== 'approved' || isSellerOnly, className: 'text-success-content-soft hover:bg-success-surface' },
              { label: 'Clonar', icon: <Copy size={14} />, onClick: () => onClone(o) },
              { label: 'Anular', icon: <X size={14} />, onClick: () => onCancel(o), hidden: !['draft', 'approved'].includes(o.status), className: 'text-danger-content-soft hover:bg-danger-surface' },
            ]} />
          </div>
        )
      } },
  ]
}

// ─── SalesOrdersPage ──────────────────────────────────────────────────────────

export function SalesOrdersPage() {
  const locale = useTenantLocale()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  // El orden va al SERVIDOR (`?ordering=`): esta lista la pagina el backend,
  // y ordenar en el navegador ordenaría solo la página visible.
  const [orden, setOrden] = useState<SortState | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)

  const { can, isViewOwn } = usePermission()
  const isSellerOnly = isViewOwn('sales.orders')
  const { user } = useAuthStore()

  const { data, isLoading } = useSalesOrders({ ordering: sortStateToParam(orden),
    search: search || undefined,
    status: statusFilter || undefined,
    page,
    page_size: 20,
  })

  const toast = useToast()
  const approveMutation = useApproveSalesOrder()
  const invoiceMutation = useInvoiceSalesOrder()
  const cancelMutation = useCancelSalesOrder()
  const cloneMutation = useCloneSalesOrder()

  const orders = data?.orders || []
  const total = data?.count || 0
  const pages = data?.pages || 1

  const handleView = (order: SalesOrderListItem) => {
    setDetailId(order.id)
  }

  const handleEdit = (order: SalesOrderListItem) => {
    navigate(`/sales/orders/${order.id}/edit`)
  }

  // Las tres acciones del kebab pueden ser rechazadas por el backend (estado
  // inválido, pedido propio, permiso). Sin `onError` el rechazo no dejaba rastro
  // en la pantalla: en producción se vieron 4 clics en 14 segundos sobre
  // "Aprobar" porque nada indicaba que el pedido había rebotado.
  const handleApprove = (order: SalesOrderListItem) => {
    approveMutation.mutate(order.id, {
      onSuccess: () => toast.success(`Pedido ${order.order_number} aprobado.`),
      onError: (e) => toast.error(extractErrorMessage(e, 'No se pudo aprobar el pedido.')),
    })
  }

  const handleInvoice = (order: SalesOrderListItem) => {
    invoiceMutation.mutate(order.id, {
      onSuccess: () => toast.success(`Pedido ${order.order_number} facturado.`),
      onError: (e) => toast.error(extractErrorMessage(e, 'No se pudo facturar el pedido.')),
    })
  }

  // Anular abre el diálogo con motivo obligatorio; antes anulaba al primer clic.
  const [anulando, setAnulando] = useState<SalesOrderListItem | null>(null)
  const handleCancel = (order: SalesOrderListItem) => setAnulando(order)
  const confirmarAnulacion = (reason: string) => {
    if (!anulando) return
    const order = anulando
    cancelMutation.mutate({ id: order.id, reason }, {
      onSuccess: () => { setAnulando(null); toast.success(`Pedido ${order.order_number} anulado.`) },
      onError: (e) => toast.error(extractErrorMessage(e, 'No se pudo anular el pedido.')),
    })
  }

  const handleClone = (order: SalesOrderListItem) => {
    cloneMutation.mutate(order.id, {
      onSuccess: (newOrder) => navigate(`/sales/orders/${newOrder.id}/edit`),
    })
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        title="Pedidos de Venta"
        subtitle={`${total} ${total === 1 ? 'pedido' : 'pedidos'} registrados`}
        help={HELP['sales.orders']}
      >
        <button
          onClick={() => navigate('/sales/orders/new')}
          className="bg-primary text-on-primary px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-hover transition-colors flex items-center gap-2"
        >
          <Plus size={15} />
          Nuevo Pedido
        </button>
      </PageHeader>

      {/* Filtros */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        {/* Buscador */}
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle pointer-events-none" />
          <input aria-label="Buscar"
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            placeholder="Buscar..."
            className="w-64 px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200 placeholder:text-content-subtle pl-8"
          />
        </div>

        {/* Status toggles */}
        <div className="flex rounded-lg border border-divider overflow-hidden bg-surface">
          {STATUS_FILTERS.map(opt => (
            <button
              key={opt.value}
              onClick={() => { setStatusFilter(opt.value); setPage(1) }}
              className={`px-3 py-2 text-sm transition-colors whitespace-nowrap
                ${statusFilter === opt.value ? 'bg-primary text-on-primary' : 'text-content-muted hover:bg-soft'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <TableSkeleton rows={8} cols={6} />
        ) : orders.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="No hay pedidos"
            hint={statusFilter || search
              ? 'Prueba con otros filtros o términos de búsqueda.'
              : 'Crea el primer pedido con el botón de arriba.'}
          />
        ) : (
          <DataTable<SalesOrderListItem>
            {...ordenControlado(orden, s => { setOrden(s); setPage(1) })}
            columns={columnasDePedido({
              isSellerOnly,
              currentUserId: user?.id,
              onView: handleView,
              onEdit: handleEdit,
              onApprove: handleApprove,
              onInvoice: handleInvoice,
              onClone: handleClone,
              onCancel: handleCancel,
            })}
            rows={orders}
            rowKey={o => o.id}
            minWidth={1100}
            surface={false}
            onRowClick={handleView}
            caption="Pedidos de venta y su estado"
          />
        )}
      </div>

      {/* Paginación */}
      {pages > 1 && (
        <Pagination
          page={page}
          pageSize={20}
          total={total}
          onPageChange={setPage}
          locale={locale}
        />
      )}

      {/* Modal detalle */}
      {detailId && (
        <SalesOrderDetailModal orderId={detailId} onClose={() => setDetailId(null)} />
      )}

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
      {anulando && (
        <AnularPedidoDialog
          orderNumber={anulando.order_number}
          isPending={cancelMutation.isPending}
          onConfirm={confirmarAnulacion}
          onClose={() => setAnulando(null)}
        />
      )}
    </div>
  )
}
